import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { driverService } from "@/services/api";
import { toast } from "sonner";

const EMPTY = { fullName: "", email: "", phone: "", status: "available" };

// Handles both "Add Driver" and "Edit Driver" — pass a `driver` prop to
// edit an existing one, omit it to create a new one.
export function AddDriverDialog({ open, onOpenChange, driver, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const isEdit = !!driver;

  useEffect(() => {
    if (!open) return;
    setForm(
      driver
        ? { fullName: driver.name || "", email: driver.email || "", phone: driver.phone || "", status: driver.status || "available" }
        : EMPTY
    );
  }, [open, driver]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await driverService.update(driver.id, form);
        toast.success("Driver updated");
      } else {
        await driverService.create(form);
        toast.success("Driver added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save driver");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-driver-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Driver" : "Add Driver"}</DialogTitle>
          <DialogDescription>{isEdit ? "Update this driver's details." : "Register a new driver."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Full name</Label><Input placeholder="Jane Smith" required value={form.fullName} onChange={set("fullName")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" placeholder="jane@company.com" value={form.email} onChange={set("email")} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input placeholder="+1 555 0100" value={form.phone} onChange={set("phone")} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="on-trip">On trip</SelectItem>
                <SelectItem value="off-duty">Off duty</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="add-driver-submit">
              {saving ? (isEdit ? "Saving…" : "Adding…") : isEdit ? "Save Changes" : "Add Driver"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
